import { API_ORIGIN, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'


export type EmailInviteSummary = {
  id: string
  email: string
  status: string
  created_at: string
  expires_at: string
  last_sent_at: string
  resend_count: number
  has_account: boolean
  accepted_application_id?: string | null
}

export type EmailInviteMatchedProfile = {
  id: string
  name: string
  headline: string
  avatar_url?: string | null
}

export type EmailInviteCheckResponse = {
  matched_profile: EmailInviteMatchedProfile | null
  pending_invite: EmailInviteSummary | null
}

export function opportunityEmailInvitesQueryKey(opportunityId: string) {
  return ['opportunity-email-invites', opportunityId] as const
}

async function fetchOpportunityEmailInvites(opportunityId: string): Promise<EmailInviteSummary[]> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/applications/opportunities/${encodeURIComponent(opportunityId)}/email-invites/`,
  )
  if (!response.ok) {
    throw new Error(`Failed to load email invites (${response.status})`)
  }
  const data = await response.json()
  return (data.invites ?? []) as EmailInviteSummary[]
}

export async function checkEmailInvite(
  opportunityId: string,
  email: string,
): Promise<EmailInviteCheckResponse> {
  const params = new URLSearchParams({
    email,
    opportunity_id: opportunityId,
  })
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/applications/email-invites/check/?${params.toString()}`,
  )
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Failed to check email')
  }
  return data as EmailInviteCheckResponse
}

export function useOpportunityEmailInvites(opportunityId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: opportunityEmailInvitesQueryKey(opportunityId ?? ''),
    queryFn: () => fetchOpportunityEmailInvites(opportunityId!),
    enabled: !!opportunityId && enabled,
    staleTime: 30_000,
  })
}

export function useEmailInviteMutations(opportunityId: string | undefined) {
  const queryClient = useQueryClient()

  const invalidate = () => {
    if (!opportunityId) return
    void queryClient.invalidateQueries({ queryKey: opportunityEmailInvitesQueryKey(opportunityId) })
    void queryClient.invalidateQueries({ queryKey: ['opportunity-applicants', opportunityId] })
  }

  const createInvite = useMutation({
    mutationFn: async (email: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/email-invites/`, {
        method: 'POST',
        body: JSON.stringify({ opportunity_id: opportunityId, email }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const err = new Error(typeof data.error === 'string' ? data.error : 'Failed to send invite') as Error & {
          status?: number
        }
        err.status = response.status
        throw err
      }
      return data as EmailInviteSummary
    },
    onSuccess: () => {
      toast.success('Invite email sent')
      invalidate()
    },
    onError: (error: Error & { status?: number }) => {
      toast.error(error.message)
    },
  })

  const resendInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/email-invites/${encodeURIComponent(inviteId)}/resend/`,
        { method: 'POST' },
      )
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to resend invite')
      }
      return data as EmailInviteSummary
    },
    onSuccess: () => {
      toast.success('Invite resent')
      invalidate()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const revokeInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/email-invites/${encodeURIComponent(inviteId)}/revoke/`,
        { method: 'POST' },
      )
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to revoke invite')
      }
      return data as EmailInviteSummary
    },
    onSuccess: () => {
      toast.success('Invite revoked')
      invalidate()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return { createInvite, resendInvite, revokeInvite }
}
